import { useState, useEffect } from "react";
import NavakranthiMainLogo from '../../assets/all-icons-logo.png';
import { apiRequest } from '../../config/apiRequest';
import API_ENDPOINTS from '../../config/apiEndPoints';
import { APP_ROUTES } from '../../config/appRoutes';
import { useData } from '../../context/DataContext';
import Cookies from 'js-cookie';
import {
    Box,
    Grid,
    Card,
    InputAdornment,
    TextField,
    IconButton,
    Typography, Stack
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AgricultureIcon from '@mui/icons-material/Agriculture';
import SnackbarMessage from '../../components/snackBar/snackBar';
import CustomButton from "../../components/buttons/customButton";
import LoginIcon from '@mui/icons-material/Login';
import keshavapurLogo from '../../assets/keshavapurLogo.jpeg';

import NavakranthiBg from '../../assets/AuthBgNavakranthi.jpg';
import KesahavapurBg from '../../assets/AuthBgKeshavapur.jpg';

function Login() {
    const [credentials, setCredentials] = useState({ userName: "", password: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const { fetchDataAfterLogin } = useData();
    const [showPassword, setShowPassword] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const mobileNumberRegex = /^[0-9]{10}$/;
    const hostname = window.location.hostname === 'keshavapur.rizee.in';

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;
        if (name === "userName") {
            newValue = value.replace(/\D/g, "").slice(0, 10);
        } else {
            newValue = value.replace(/\D/g, "").slice(0, 6);
        }

        setCredentials((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
            general: "",
        }));
    };

    useEffect(() => {
        if (hostname) {
            localStorage.setItem('client_id', 2);
        } else {
            localStorage.setItem('client_id', 1);
        }
    }, [hostname])

    const validateForm = () => {
        const { userName, password } = credentials;
        const formErrors = {};

        // Mobile validation
        if (!userName.trim()) {
            formErrors.userName = "Mobile number is required";
        } else if (!mobileNumberRegex.test(userName)) {
            formErrors.userName = "Invalid Mobile Number";
        }

        // Password validation
        if (!password.trim()) {
            formErrors.password = "Password is required";
        } else if (password.length < 6) {
            formErrors.password = "Password must be at least 6 characters";
        }

        // 🔔 Show only FIRST error in snackbar
        const firstError = Object.values(formErrors)[0];
        if (firstError) {
            setSnackbar({
                open: true,
                message: firstError,
                severity: "error",
            });
        }

        return formErrors;
    };


    const handleKeyPress = (e) => {
        if (e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
        } else {
            if (e.key === "Enter") handleSubmit(e);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await apiRequest(API_ENDPOINTS.LOGIN, 'POST', {
                username: credentials.userName,
                password: credentials.password,
                client_id: Number(localStorage.getItem("client_id")) || 1,
            });

            if (response.statusCode === 201) {
                Cookies.set('farmerToken', response.data.token); // Set cookie     
                window.location.href = APP_ROUTES.DASHBOARD;
                await fetchDataAfterLogin();
            } else {
                setErrors({ general: response.error.error });
                setSnackbar({
                    open: true,
                    message: response.error.error,
                    severity: "error",
                });
            }
        } catch (error) {
            setErrors({ general: 'Failed to login. Please try again later.' });
            setSnackbar({
                open: true,
                message: 'Failed to login. Please try again later.',
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const checkTokenAndRedirect = async () => {
            const token = Cookies.get('farmerToken');

            if (token) {
                await fetchDataAfterLogin();
                window.location.href = APP_ROUTES.DASHBOARD;
            }
        };

        checkTokenAndRedirect();

        return () => {
            setCredentials({ userName: "", password: "" });
            setErrors({});
        };
    }, []);


    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ height: "100vh", backgroundColor: "#eafff3" }}>
            <Grid container sx={{ height: "100%" }}>

                {/* LEFT SIDE */}
                <Grid
                    item size={{ xs: 12, md: 6 }}
                    sx={{
                        position: "relative",
                        display: { xs: "none", md: "flex" },
                        alignItems: "center",
                        padding: 6,
                        color: "#fff",
                        backgroundImage: {
                            md: `url(${hostname ? KesahavapurBg : NavakranthiBg})`,
                        },
                        backgroundSize: "cover",
                        backgroundPosition: "center",

                        "&::before": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            background: !hostname
                                ? "linear-gradient(rgba(22,145,76,0.85), rgba(22,145,76,0.85))"
                                : "linear-gradient(rgba(85,107,47,0.82), rgba(85,107,47,0.82))",
                            zIndex: 0,
                        },
                    }}
                >
                    <Box sx={{ position: "relative", zIndex: 1, maxWidth: 420 }}>
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: 2,
                                backgroundColor: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            <AgricultureIcon sx={{ color: "#16914c", fontSize: 32 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} fontSize="bold" gutterBottom sx={{ textTransform: 'uppercase' }}>
                            {
                                hostname ? "Keshavapur Farmers Producer Company Limited" : "Navakranthi Farmers Producer Company Limited"
                            }
                        </Typography>

                        <Typography mb={4}>
                            Empowering Farmers with Smart Project & Field Management
                        </Typography>

                        <Stack spacing={2}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <ShowChartOutlinedIcon />
                                <Typography fontSize="bold">Manage Training Plans & Sessions</Typography>
                            </Stack>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <LockOutlinedIcon />
                                <Typography fontSize="bold">Track Farmer Adoption Progress</Typography>
                            </Stack>

                            <Stack direction="row" spacing={2} alignItems="center">
                                <GroupsOutlinedIcon />
                                <Typography fontSize="bold">Monitor Soil Reports & Analysis</Typography>
                            </Stack>
                        </Stack>
                    </Box>
                </Grid>

                {/* RIGHT SIDE */}
                <Grid
                    item size={{ xs: 12, md: 6 }}
                    sx={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",

                        backgroundImage: {
                            xs: `url(${hostname ? KesahavapurBg : NavakranthiBg})`,
                            md: "none",
                        },
                        backgroundSize: "cover",
                        backgroundPosition: "center",

                        "&::before": {
                            content: { xs: '""', md: "none" },
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(rgba(22,145,76,0.85), rgba(22,145,76,0.85))",
                            zIndex: 1,
                        },
                    }}
                >
                    {/* CARD */}
                    <Card
                        elevation={12}
                        sx={{
                            width: "100%",
                            maxWidth: 500,
                            p: 5,
                            mx: 2,
                            borderRadius: 3,
                            position: "relative",
                            zIndex: 2,
                            textAlign: "center",
                            '&:hover': {
                                boxShadow: '0 6px 20px #044412ff',
                            },
                            '&:hover::before': {
                                opacity: 1,
                                filter: 'blur(20px)',
                            },
                        }}
                    >

                        {/* LOGO */}
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            <Box
                                component="img"
                                src={hostname ? keshavapurLogo : NavakranthiMainLogo}
                                alt="Navakranthi Logo"
                                sx={{
                                    height: 140,
                                    width: "auto",
                                    objectFit: "contain",
                                }}
                            />
                        </Box>

                        <Grid container spacing={1}>
                            <Grid item size={{ xs: 12, md: 12 }}>
                                <Typography sx={{ fontWeight: 600, textAlign: "start" }}>Mobile Number</Typography>
                                <TextField
                                    name="userName"
                                    size="small"
                                    type="text"
                                    variant="outlined"
                                    placeholder="Enter mobile number"
                                    value={credentials.userName}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyPress}
                                    fullWidth
                                    error={!!errors.userName}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocalPhoneIcon color="#000" fontWeight="600" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        maxLength: 10,
                                    }}
                                />
                            </Grid>

                            <Grid item size={{ xs: 12, md: 12 }}>
                                <Typography sx={{ fontWeight: 600, textAlign: "start" }}>Password</Typography>
                                <TextField
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    size="small"
                                    variant="outlined"
                                    placeholder="Enter Password"
                                    value={credentials.password}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyPress}
                                    fullWidth
                                    error={!!errors.password}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutlinedIcon color="#000" fontWeight="600" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    inputProps={{
                                        maxLength: 6,
                                    }}
                                />
                            </Grid>

                            <Grid item size={{ xs: 12, md: 12 }} mt={1}>
                                <CustomButton
                                    status={loading === true ? "loading" : "idle"}
                                    onClick={handleSubmit}
                                    label="Login"
                                    icon={<LoginIcon />}
                                    color={hostname ? "#89422a" : "#14532D"}
                                    loadingLabel="Logining.."
                                />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            </Grid>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </Box>
    );
}
export default Login;